import { ProductFeeType } from '@msslib/constants';
import { Injectable } from '@angular/core';
import { BridgingProductsHelper } from 'apps/shared/src/helpers/bridging-products.helper';
import {
  DropdownFilterType,
  ISimpleGridColumn,
  SimpleGridColumnType,
  SimpleGridFilterOperator,
} from '@msslib/models/simple-grid';
import {
  IUiProductsSearchModel,
  ProductResultsConfigurationRequestModel,
  ProductResultsConfigurationResponseModel,
  ProductResultsOrder,
  ProductResultsOrderDirection,
  ProductsResultsColumn,
} from 'apps/clubhub/src/app/ignite/models/products';
import { ProductsSearchService } from 'apps/clubhub/src/app/ignite/services';
import { ProductsUtils } from 'apps/shared/src/utils/products.utils';
import { LendingTypeCode, Product } from 'apps/shared/src/models';
import { ClubHubDataService } from '@msslib/services';
import { Subject, of, tap } from 'rxjs';
import { debounceWait } from '@msslib/helpers/rxjs-helpers';
import { AuthorizeService } from '@msslib/services/authorize.service';

type ColumnWithEnum = ISimpleGridColumn & { columnEnum: ProductsResultsColumn | null };

const affordabilityMetUnknownValue = '';

@Injectable({
  providedIn: 'root',
})
export class ProductsResultsService {
  /** Use `next` to set a value on this configuration, and after a debounce period it will be sent to the server. */
  private updateUserResultConfiguration$ =
    new Subject<[LendingTypeCode, ProductResultsConfigurationRequestModel]>();

  public constructor(
    private bridgingProductsHelper: BridgingProductsHelper,
    private productsSearchService: ProductsSearchService,
    private productsUtils: ProductsUtils,
    private dataService: ClubHubDataService,
    private authService: AuthorizeService,
  ) {
    if (this.authService.isLoggedIn) {
      // Wait for 5 secs or for the previous request to complete (whichever is longer) before attempting to send another
      debounceWait(this.updateUserResultConfiguration$, 5000, ([lendingTypeCode, request]) =>
        this.dataService.put<never>(`Product/UserResultsConfiguration/${lendingTypeCode}`, request));
    }

    productsSearchService.loadNewResultsConfiguration$?.subscribe(([lendingTypeCode, response]) => {
      this.loadResultsConfigurationResponse(lendingTypeCode, response);
    });
  }

  public readonly trueCostInitialPeriodLabel = 'True Cost \n(xx months)';

  public readonly trueCostInitialPeriodHeaderTooltip
    = 'Products have been sorted in accordance to the FCA cheapest rule';

  private readonly resAndBtlProductsColumns: ColumnWithEnum[] = [
    {
      name: 'lender',
      label: 'Lender',
      columnEnum: ProductsResultsColumn.Lender,
      headerFilter: 'select',
      sortable: false,
      pluralLabel: 'Lenders',
      headerStyle: { 'max-width': '166px' },
    },
    {
      name: 'productDescription',
      label: 'Product Description',
      columnEnum: ProductsResultsColumn.ProductDescription,
      render: v => v ?? 'Unavailable',
      classList: 'product-no-wrap',
      headerFilter: 'input',
      sortable: false,
      headerStyle: { 'max-width': '130px' },
    },
    {
      name: 'productCode',
      label: 'Product Code',
      columnEnum: ProductsResultsColumn.ProductCode,
      classList: 'product-no-wrap',
      headerFilter: 'input',
      sortable: false,
      headerStyle: { 'max-width': '90px' },
    },
    {
      name: 'type',
      label: 'Type',
      columnEnum: ProductsResultsColumn.ProductType,
      dropdownFilter: DropdownFilterType.None,
      sortable: false,
      classList: 'text-center',
      headerStyle: { 'min-width': '36px' },
      headerTooltip: 'Type',
      icon: 'fa fa-info-circle',
    },
    {
      name: 'maxLtv',
      label: 'Max LTV',
      columnEnum: ProductsResultsColumn.MaxLtv,
      dropdownFilter: DropdownFilterType.None,
      suffix: '%',
      type: SimpleGridColumnType.Number,
      classList: 'text-center',
    },
    {
      name: 'initialPeriod',
      label: 'Initial Period (Months)',
      columnEnum: ProductsResultsColumn.InitialPeriod,
      dropdownFilter: DropdownFilterType.None,
      discreteFilterIcon: 'fa-calendar-alt',
      type: SimpleGridColumnType.Number,
      classList: 'text-center',
      sortBy: p => p.forTerm ? Number.MAX_SAFE_INTEGER : p.initialPeriod,
    },
    {
      name: 'initialRateValue',
      label: 'Initial Rate',
      columnEnum: ProductsResultsColumn.InitialRate,
      dropdownFilter: DropdownFilterType.None,
      suffix: '%',
      type: SimpleGridColumnType.Number,
      classList: 'text-center',
    },
    {
      name: 'initialRateType',
      label: 'Rate Type',
      columnEnum: ProductsResultsColumn.RateType,
      dropdownFilter: DropdownFilterType.None,
      classList: 'text-center',
    },
    {
      name: 'revertRate',
      label: 'Revert Rate',
      columnEnum: ProductsResultsColumn.RevertRate,
      dropdownFilter: DropdownFilterType.None,
      suffix: '%',
      type: SimpleGridColumnType.Number,
      classList: 'text-center',
      sortBy: p => this.productsUtils.getRevertRateColumnValue(p as Product) ?? 0,
    },
    {
      name: 'initialMonthlyPayment',
      label: 'Initial Monthly Payment',
      columnEnum: ProductsResultsColumn.InitialMonthlyPayment,
      dropdownFilter: DropdownFilterType.None,
      type: SimpleGridColumnType.Number,
      classList: 'text-center',
    },
    {
      name: 'monthlyPaymentRevert',
      label: 'Monthly Payment Revert',
      columnEnum: ProductsResultsColumn.MonthlyPaymentRevert,
      dropdownFilter: DropdownFilterType.None,
      type: SimpleGridColumnType.Number,
      classList: 'text-center',
    },
    {
      name: 'trueCostInitialPeriod',
      label: 'True Cost (Initial Period)',
      columnEnum: ProductsResultsColumn.TrueCostInitialPeriod,
      dropdownFilter: DropdownFilterType.None,
      type: SimpleGridColumnType.Number,
      classList: 'text-center',
      sortBy: r => r?.trueCostInitialPeriod ?? Infinity,
      headerTooltip: this.trueCostInitialPeriodHeaderTooltip,
    },
    {
      name: 'trueCostCustomPeriod',
      label: this.trueCostInitialPeriodLabel,
      columnEnum: ProductsResultsColumn.TrueCostCustomPeriod,
      dropdownFilter: DropdownFilterType.None,
      type: SimpleGridColumnType.Number,
      classList: 'text-center',
      sortBy: r => r?.trueCostCustomPeriod ?? Infinity,
      headerStyle: { 'min-width': '80px', 'white-space': 'pre' },
    },
    {
      name: 'trueCost',
      label: 'True Cost (Term)',
      columnEnum: ProductsResultsColumn.TrueCostTerm,
      dropdownFilter: DropdownFilterType.None,
      type: SimpleGridColumnType.Number,
      classList: 'text-center',
      sortBy: r => r?.trueCost ?? Infinity,
    },
    {
      name: 'outstandingBalance',
      label: 'Outstanding Balance',
      columnEnum: ProductsResultsColumn.OutstandingBalance,
      dropdownFilter: DropdownFilterType.None,
      type: SimpleGridColumnType.Number,
      classList: 'text-center',
      sortBy: r => r?.outstandingBalance ?? Infinity,
    },
    {
      name: 'earlyRepaymentChargesErcApplied',
      label: 'ERC',
      columnEnum: ProductsResultsColumn.Erc,
      headerStyle: { 'min-width': '40px' },
      dropdownFilter: DropdownFilterType.None,
      sortable: false,
      classList: 'text-center',
    },
    {
      name: 'cashback',
      label: 'Cashback',
      columnEnum: ProductsResultsColumn.Cashback,
      dropdownFilter: DropdownFilterType.None,
      type: SimpleGridColumnType.Number,
      classList: 'text-center',
      sortBy: r => r.cashback ?? -1,
    },
    {
      name: 'product_fee_amount_payable',
      label: 'Total Fees',
      columnEnum: ProductsResultsColumn.TotalFees,
      dropdownFilter: DropdownFilterType.None,
      type: SimpleGridColumnType.Number,
      classList: 'text-center',
      sortBy: r => this.productsUtils.calculateTotalFees(r as Product, this.productsSearchService),
    },
    {
      name: 'arrangementFee',
      label: 'Arrangement Fee',
      columnEnum: ProductsResultsColumn.ArrangementFee,
      dropdownFilter: DropdownFilterType.None,
      type: SimpleGridColumnType.Number,
      classList: 'text-center',
      sortBy: r => r.fees[ProductFeeType.Arrangement] ?? -1,
    },
    {
      name: 'lenderOutcomeResult',
      label: 'Criteria',
      columnEnum: ProductsResultsColumn.Criteria,
      classList: 'text-center',
      sortable: false,
      dropdownFilter: DropdownFilterType.None,
      discreteFilter: [
        { label: 'Yes', value: 'Y', icon: 'far fa-lg fa-check-circle text-success' },
        { label: 'Refer', value: 'R', icon: 'fa-lg fa-exchange-alt text-warning' },
        { label: 'No', value: 'N', icon: 'far fa-lg fa-times-circle text-danger' },
      ],
      discreteMultiSelect: true,
    },
    {
      name: 'lenderAffordabilityMet',
      label: 'Affordability Met',
      columnEnum: ProductsResultsColumn.AffordabilityMet,
      classList: 'text-center',
      sortable: false,
      dropdownFilter: DropdownFilterType.None,
      discreteFilter: this.getAffordabilityMetFilter(),
      discreteMultiSelect: true,
      filterHandler: (value, filter) => {
        if (filter.op === SimpleGridFilterOperator.In && Array.isArray(filter.value)) {
          // For the 'unknown' filter, we ALSO need to match on empty strings, nulls, or undefined.
          if (filter.value.includes(affordabilityMetUnknownValue) && (value === null || value === undefined)) {
            return true;
          }

          return filter.value.includes(value);
        }

        return true;
      },
    },
    // {
    //   name: 'isMatch',
    //   label: 'Filter Match',
    //   icon: 'fa fa-info-circle',
    //   dropdownFilter: DropdownFilterType.None,
    //   classList: 'text-center',
    //   discreteFilter: [{
    //     label: 'Only show complete matches',
    //     icon: 'fa-check',
    //     value: true,
    //   }],
    //   filterHandler: (_val, _op, r: Product) => this.isProductMatchFilterFunc?.(r),
    //   sortBy: (r: Product) => this.isProductMatchFilterFunc?.(r),
    //   sortLabels: { desc: { label: 'Show complete matches first', icon: 'fa-check' } },
    //   sortDirRestrict: 'desc',
    //   clearSortButton: true,
    //   headerTooltipClass: 'match-tooltip',
    //   headerTooltip: '✓ : Checked ? : Unknown',
    // },
    {
      name: 'compare',
      label: 'Compare',
      columnEnum: ProductsResultsColumn.Compare,
      dropdownFilter: DropdownFilterType.None,
      sortable: false,
      classList: 'text-center',
    },
    {
      name: 'docs',
      label: 'Docs',
      columnEnum: ProductsResultsColumn.Docs,
      dropdownFilter: DropdownFilterType.None,
      sortable: false,
      classList: 'text-center',
      hidden: true,
    },
    {
      // This is a fake column that we use that handles the sidebar filters
      name: '_sidebarFilters',
      columnEnum: null,
      hidden: true,
    },
  ];

  private readonly btlProductsColumns: ColumnWithEnum[] = [
    ...this.resAndBtlProductsColumns,
    {
      classList: 'text-center',
      columnEnum: ProductsResultsColumn.MaxAffordableLoan,
      dropdownFilter: DropdownFilterType.None,
      icon: 'fa fa-info-circle',
      label: 'Max Affordable Loan',
      name: 'maximumAffordableLoanAmount',
      sortable: true,
      sortBy: r => r?.maximumAffordableLoanAmount ?? -Number.MAX_SAFE_INTEGER,
      type: SimpleGridColumnType.Number,
      headerTooltip: 'All maximum loan sizes are calculated using products currently on offer from each lender. ' +
        'Some of the products used may have significant fees associated with them - please check the product ' +
        'details, or with a lender directly, before making any recommendations to your clients',
    },
    {
      classList: 'text-center',
      columnEnum: ProductsResultsColumn.RentalRequired,
      dropdownFilter: DropdownFilterType.None,
      headerTooltip: 'Rent required to access this product based on the loan amount requested',
      icon: 'fa fa-info-circle',
      label: 'Rental Required',
      name: 'requiredMonthlyRentalIncome',
      sortable: true,
      sortBy: r => r?.requiredMonthlyRentalIncome ?? -Number.MAX_SAFE_INTEGER,
      type: SimpleGridColumnType.Number,
    },
  ];

  private readonly bridgingProductsColumns: ColumnWithEnum[] = [
    {
      name: 'lender',
      label: 'Lender',
      columnEnum: ProductsResultsColumn.Lender,
      headerFilter: 'select',
      sortable: false,
      pluralLabel: 'Lenders',
      headerStyle: { 'max-width': '166px' },
    },
    {
      name: 'description',
      label: 'Product Description',
      columnEnum: ProductsResultsColumn.ProductDescription,
      render: v => v ?? 'Unavailable',
      classList: 'product-no-wrap',
      headerFilter: 'input',
      sortable: false,
      headerStyle: { 'max-width': '130px' },
    },
    {
      name: 'code',
      label: 'Product Code',
      columnEnum: ProductsResultsColumn.ProductCode,
      headerFilter: 'input',
      sortable: false,
      headerStyle: { 'max-width': '90px' },
    },
    {
      name: 'maxLtv',
      label: 'Max LTV Gross',
      columnEnum: ProductsResultsColumn.MaxLtv,
      dropdownFilter: DropdownFilterType.None,
      suffix: '%',
      type: SimpleGridColumnType.Number,
      classList: 'text-center',
    },
    {
      name: 'maxTerm',
      label: 'Max Duration (Months)',
      columnEnum: ProductsResultsColumn.MaxDuration,
      suffix: ' months',
      dropdownFilter: DropdownFilterType.None,
      discreteFilter: true,
      classList: 'text-center',
      discreteFilterIcon: 'fa-calendar-alt',
      type: SimpleGridColumnType.Number,
    },
    {
      name: 'rates',
      label: 'Monthly rate',
      columnEnum: ProductsResultsColumn.MonthlyRate,
      suffix: '%',
      dropdownFilter: DropdownFilterType.None,
      sortable: false,
      classList: 'text-center',
      type: SimpleGridColumnType.Number,
    },
    {
      name: 'productType',
      label: 'Rate Type',
      columnEnum: ProductsResultsColumn.RateType,
      dropdownFilter: DropdownFilterType.None,
      sortable: false,
      classList: 'text-center',
      discreteFilter: this.bridgingProductsHelper.productTypesDiscreteFilter(),
    },
    {
      name: 'monthlyRate',
      label: 'Monthly Payment',
      columnEnum: ProductsResultsColumn.InitialMonthlyPayment,
      type: SimpleGridColumnType.Number,
      dropdownFilter: DropdownFilterType.None,
      sortable: true,
      classList: 'text-center',
    },
    {
      name: 'grossLoan',
      label: 'Gross Loan',
      columnEnum: ProductsResultsColumn.GrossLoan,
      dropdownFilter: DropdownFilterType.None,
      sortable: true,
      type: SimpleGridColumnType.Number,
      classList: 'text-center',
      headerTooltip: 'including all fees added to the loan, charges or interest',
    },
    {
      name: 'grossLtv',
      label: 'Gross LTV',
      columnEnum: ProductsResultsColumn.GrossLtv,
      dropdownFilter: DropdownFilterType.None,
      suffix: '%',
      type: SimpleGridColumnType.Number,
      classList: 'text-center',
    },
    {
      name: 'trueCost',
      label: 'True Cost (Term)',
      columnEnum: ProductsResultsColumn.TrueCostTerm,
      dropdownFilter: DropdownFilterType.None,
      type: SimpleGridColumnType.Number,
      classList: 'text-center',
    },
    {
      name: 'exitFee',
      label: 'Exit fee',
      columnEnum: ProductsResultsColumn.ExitFee,
      dropdownFilter: DropdownFilterType.None,
      type: SimpleGridColumnType.Number,
      classList: 'text-center',
    },
    {
      name: 'totalFee',
      label: 'Total Fees',
      columnEnum: ProductsResultsColumn.TotalFees,
      dropdownFilter: DropdownFilterType.None,
      type: SimpleGridColumnType.Number,
      classList: 'text-center',
    },
    {
      name: 'aprc',
      label: 'APRC',
      columnEnum: ProductsResultsColumn.Aprc,
      suffix: '%',
      dropdownFilter: DropdownFilterType.None,
      type: SimpleGridColumnType.Number,
      classList: 'text-center',
    },
    // {
    //   name: 'isMatch',
    //   label: 'Filter Match',
    //   icon: 'fa fa-info-circle',
    //   dropdownFilter: DropdownFilterType.None,
    //   classList: 'text-center',
    //   discreteFilter: [{
    //     label: 'Only show complete matches',
    //     icon: 'fa-check',
    //     value: true,
    //   }],
    //   filterHandler: (_val, _op, r: Product) => this.isProductMatchFilterFunc?.(r),
    //   sortBy: (r: Product) => this.isProductMatchFilterFunc?.(r),
    //   sortLabels: { desc: { label: 'Show complete matches first', icon: 'fa-check' } },
    //   sortDirRestrict: 'desc',
    //   clearSortButton: true,
    //   headerTooltipClass: 'match-tooltip',
    //   headerTooltip: '✓ : Checked ? : Unknown',
    // },
    {
      name: 'compare',
      label: 'Compare',
      columnEnum: ProductsResultsColumn.Compare,
      dropdownFilter: DropdownFilterType.None,
      sortable: false,
      classList: 'text-center',
    },
    {
      name: 'docs',
      label: 'Docs',
      columnEnum: ProductsResultsColumn.Docs,
      dropdownFilter: DropdownFilterType.None,
      sortable: false,
      classList: 'text-center',
      hidden: true,
    },
    {
      // This is a fake column that we use that handles the sidebar filters
      name: '_sidebarFilters',
      columnEnum: null,
      hidden: true,
    },
  ];

  private readonly columnConfigResponses = new Map<string, ProductResultsConfigurationResponseModel>();

  public setTrueCostHeader(
    model: IUiProductsSearchModel,
    productColumns: ISimpleGridColumn[],
    isBridgingType = false,
    forceTitleUpdate = false,
  ): void {
    if (!isBridgingType) {
      if (forceTitleUpdate) {
        const trueCostInitialPeriodColumn = productColumns
          .find(col => col.name === 'trueCostCustomPeriod');
        if (trueCostInitialPeriodColumn) {
          trueCostInitialPeriodColumn.label = this.trueCostInitialPeriodLabel;
        }
      }
      return;
    }

    const trueCostColumn = productColumns
      .find(col => col.name === 'trueCost');
    const loanTermMonths = model?.bridging?.loanTermMonths;

    if (trueCostColumn) {
      if (loanTermMonths) {
        const label = loanTermMonths === 1
          ? `True Cost \n(${loanTermMonths} Month)`
          : `True Cost \n(${loanTermMonths} Months)`;

        trueCostColumn.label = label;
      } else {
        trueCostColumn.label = 'True Cost (Term)';
      }
    }
  }

  public getDocsTooltipMessage(
    isOutcomeResults: boolean,
    hasTrueCost: boolean,
    isDirectOnlyProduct: boolean,
  ): string {
    if (isOutcomeResults) {
      return 'Please Re-run case to enable Documents';
    }

    if (!hasTrueCost) {
      return 'In order to generate Documents, the following information must be entered: Method of Repayment, '
        + 'Mortgage Term.';
    } else if (isDirectOnlyProduct) {
      return 'Documents are unavailable for Direct Only products';
    }

    return '';
  }

  public isDocsButtonEnabled(
    isOutcomeResults: boolean,
    isDirectProduct: boolean,
    isProductHasTrueCost: boolean,
    isAffordabilitySearchIdDefined: boolean,
    caseReran: boolean,
    caseEditedAll: boolean,
  ): boolean {
    if (isOutcomeResults || isDirectProduct) {
      return false;
    }

    return isProductHasTrueCost &&
      (!isAffordabilitySearchIdDefined || caseReran || caseEditedAll);
  }

  public getAffordabilityMetFilter() {
    return [
      { label: 'Yes', value: 'Pass', icon: 'far fa-lg fa-check-circle text-success' },
      { label: 'No', value: 'Fail', icon: 'far fa-lg fa-times-circle text-danger' },
      { label: 'Unknown', value: affordabilityMetUnknownValue, icon: 'far fa-lg fa-question-circle text-warning' },
    ];
  }

  public initialiseUserColumnConfiguration(lendingTypeCode: LendingTypeCode) {
    if (!this.authService.isLoggedIn) {
      return of();
    }

    this.productsSearchService.productResultsConfigLoading$.next(true);
    return this.dataService
      .get<ProductResultsConfigurationResponseModel>(`Product/UserResultsConfiguration/${lendingTypeCode}`)
      .pipe(tap(response => {
        this.loadResultsConfigurationResponse(lendingTypeCode, response);
        this.productsSearchService.productResultsConfigLoading$.next(false);
      }));
  }

  /** @param lendingType Name OR code of the lending type. */
  public getColumnsForLendingType(lendingType: string): ColumnWithEnum[] {
    switch (lendingType.toUpperCase()) {
      case LendingTypeCode.Res:
      case 'RESIDENTIAL':
        return this.resAndBtlProductsColumns;

      case LendingTypeCode.Btl:
      case 'BUYTOLET':
      case 'BUY TO LET':
        return this.btlProductsColumns;

      case LendingTypeCode.Bdg:
      case 'BRIDGING':
        return this.bridgingProductsColumns;

      default:
        throw new Error(`Unknown lending type code '${lendingType}'.`);
    }
  }

  private getProductsOrder(columns: ColumnWithEnum[]) {
    const order = this.productsSearchService.productsOrder;
    if (!order) {
      return null;
    }

    const column = columns.find(r => r.name === order.sortOn);
    if (!column) {
      return null;
    }

    return {
      sortOn: column.columnEnum,
      sortDir: this.getProductsOrderFromString(order.sortDir),
    } as ProductResultsOrder;
  }

  private getProductsOrderFromString(sortDir: 'asc' | 'desc' | null) {
    switch (sortDir) {
      case 'asc': return ProductResultsOrderDirection.Asc;
      case 'desc': return ProductResultsOrderDirection.Desc;
      default: return null;
    }
  }

  private getProductsOrderDirectionFromEnum(dir: ProductResultsOrderDirection) {
    switch (dir) {
      case ProductResultsOrderDirection.Asc: return 'asc';
      case ProductResultsOrderDirection.Desc: return 'desc';
    }
  }

  public getSimpleGridProductsSort(lendingTypeCode: LendingTypeCode, productResultsOrder: ProductResultsOrder) {
    const column = this.getColumn(lendingTypeCode, productResultsOrder.sortOn);
    const dir = this.getProductsOrderDirectionFromEnum(productResultsOrder.sortDir) as 'asc' | 'desc';
    if (!column || !dir) {
      return null;
    }

    return {
      sortOn: column.name,
      dir,
    };
  }

  private getColumn(lendingTypeCode: LendingTypeCode, columnEnum: ProductsResultsColumn): ColumnWithEnum {
    const column = this.getColumnsForLendingType(lendingTypeCode).find(c => c.columnEnum === columnEnum);
    if (!column) {
      throw new Error('Could not find column.');
    }
    return column;
  }

  public getColumnConfig(lendingTypeCode: LendingTypeCode, column: ProductsResultsColumn) {
    return this.columnConfigResponses.get(lendingTypeCode)?.columns.find(c => c.column === column);
  }

  public async setColumnVisible(lendingTypeCode: LendingTypeCode, columnEnum: ProductsResultsColumn, visible: boolean) {
    const column = this.getColumn(lendingTypeCode, columnEnum);
    column.hidden = !visible;

    this.updateUserResultConfiguration$
      .next([lendingTypeCode, this.getResultConfigurationRequestModel(this.getColumnsForLendingType(lendingTypeCode))]);
  }

  public async setColumnOrder(lendingTypeCode: LendingTypeCode, orderedColumnNames: ProductsResultsColumn[]) {
    const columns = this.getColumnsForLendingType(lendingTypeCode);
    columns.sort((a, b) => {
      const aIdx = a.columnEnum !== null ? orderedColumnNames.indexOf(a.columnEnum) : Infinity;
      const bIdx = b.columnEnum !== null ? orderedColumnNames.indexOf(b.columnEnum) : Infinity;
      return aIdx - bIdx;
    });

    this.updateUserResultConfiguration$.next([lendingTypeCode, this.getResultConfigurationRequestModel(columns)]);
  }

  private getResultConfigurationRequestModel(
    columns: ColumnWithEnum[],
    saveProductsOrder = false,
  ): ProductResultsConfigurationRequestModel {
    const productResultsOrder = saveProductsOrder ? this.getProductsOrder(columns) : null;
    return {
      columns: columns
        .filter(c => c.columnEnum !== null)
        .map(c => ({
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          column: c.columnEnum!,
          isVisible: !c.hidden,
        })),
      productResultsOrder: productResultsOrder,
    };
  }

  public getCurrentResultConfigurationRequestModel(
    lendingTypeCode: LendingTypeCode,
    saveProductsOrder = false,
  ): ProductResultsConfigurationRequestModel {
    const columns = this.getColumnsForLendingType(lendingTypeCode);
    return this.getResultConfigurationRequestModel(columns, saveProductsOrder);
  }

  private loadResultsConfigurationResponse(
    lendingTypeCode: LendingTypeCode,
    response: ProductResultsConfigurationResponseModel,
  ) {
    this.columnConfigResponses.set(lendingTypeCode, response);

    const columns = this.getColumnsForLendingType(lendingTypeCode);
    columns.sort((a, b) => {
      const aIdx = response.columns.findIndex(c => c.column === a.columnEnum);
      const bIdx = response.columns.findIndex(c => c.column === b.columnEnum);
      return aIdx - bIdx;
    });

    for (const column of columns) {
      if (column.columnEnum) {
        const columnConfig = response.columns.find(c => c.column === column.columnEnum);
        column.hidden = !columnConfig?.isVisible;
      }
    }
  }
}
